import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	CircularProgress,
	Snackbar,
	Alert,
	Paper,
} from "@mui/material";

// Replace these with your actual API endpoints
const API_GET_USER = "https://api.example.com/user/profile";
const API_UPDATE_USER = "https://api.example.com/user/profile";

const UserProfileSettings = () => {
	const [userData, setUserData] = useState({
		name: "",
		email: "",
		phone: "",
		address: "",
	});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [successMsg, setSuccessMsg] = useState("");
	const [showSnackbar, setShowSnackbar] = useState(false);

	// Fetch user data on component mount
	useEffect(() => {
		const fetchUserData = async () => {
			try {
				const response = await fetch(API_GET_USER);
				if (!response.ok) throw new Error("Failed to fetch user data");
				const data = await response.json();
				setUserData({
					name: data.name || "",
					email: data.email || "",
					phone: data.phone || "",
					address: data.address || "",
				});
			} catch (err) {
				setError(err.message);
			} finally {
				setLoading(false);
			}
		};
		fetchUserData();
	}, []);

	const handleChange = (field) => (e) => {
		setUserData((prev) => ({ ...prev, [field]: e.target.value }));
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const response = await fetch(API_UPDATE_USER, {
				method: "PUT", // or 'POST' based on your API
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(userData),
			});
			if (!response.ok) throw new Error("Failed to update profile");
			setSuccessMsg("Profile updated successfully!");
			setShowSnackbar(true);
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box sx={{ maxWidth: 600, mx: "auto", p: 4 }}>
			<Typography variant="h4" gutterBottom align="center" mb={4}>
				User Profile Settings
			</Typography>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
					<CircularProgress />
				</Box>
			) : (
				<Paper sx={{ p: 3, boxShadow: 3 }}>
					<Box display="flex" flexDirection="column" gap={2}>
						<TextField
							label="Name"
							value={userData.name}
							onChange={handleChange("name")}
							fullWidth
						/>
						<TextField
							label="Email"
							type="email"
							value={userData.email}
							onChange={handleChange("email")}
							fullWidth
						/>
						<TextField
							label="Phone"
							value={userData.phone}
							onChange={handleChange("phone")}
							fullWidth
						/>
						<TextField
							label="Address"
							value={userData.address}
							onChange={handleChange("address")}
							multiline
							rows={3}
							fullWidth
						/>

						<Button
							variant="contained"
							color="primary"
							onClick={handleSave}
							disabled={saving}>
							{saving ? "Saving..." : "Save Changes"}
						</Button>
					</Box>
				</Paper>
			)}

			{/* Success Snackbar */}
			<Snackbar
				open={showSnackbar}
				autoHideDuration={3000}
				onClose={() => setShowSnackbar(false)}>
				<Alert severity="success" sx={{ width: "100%" }}>
					{successMsg}
				</Alert>
			</Snackbar>

			{/* Error Snackbar */}
			{error && (
				<Snackbar
					open={Boolean(error)}
					autoHideDuration={4000}
					onClose={() => setError("")}>
					<Alert severity="error" sx={{ width: "100%" }}>
						{error}
					</Alert>
				</Snackbar>
			)}
		</Box>
	);
};

export default UserProfileSettings;
